// src/pages/RegistrationFuiteDonnees/components/RegistrationForm.tsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { register, successTemp, tempRegister } from "features/account";
import './RegistrationForm.scss';
import FormInput from "./UI/FormInput";
import Checkbox from "./UI/Checkbox";
import Button from "./UI/Button";
import { ReactComponent as IconUser } from "assets/img/icons/icon_user2.svg";
import { ReactComponent as IconPhone } from "assets/img/icons/icon_phone.svg";
import { debounce } from "shared/lib/debounce";
import Alert from "shared/ui/Alert";
import {
  decryptApiBodyToStringWordPress,
  encryptApiBodyFromStringWordPress,
} from "shared/api/cryptApiBody";
import { localStorageUtil } from "shared/lib/localStorage_util";

interface RegistrationFormProps {
  globalSetSteps: any;
  globalCurrentStep: number;
  setGlobalEmail?: any;
  globalEmail?: string | null;
}

const RegistrationForm: React.FC<RegistrationFormProps> = ({
                                                             globalCurrentStep,
                                                             globalSetSteps, setGlobalEmail, globalEmail
                                                           }) => {
  // Step state
  const [currentStep, setCurrentStep] = useState(0);
  const [isTermsAccepted, setIsTermsAccepted] = useState(false);
  const [sessionPageToken, setSessionPageToken] = useState<string>('');
  const [isInitialized, setIsInitialized] = useState(false);

  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  // Store form values separately for debounced updates
  const formValuesRef = useRef({
    firstname: "",
    lastname: "",
    phoneNumber: "",
  });

  // Registration data state
  const [registrationData, setRegistrationData] = useState({
    firstname: "",
    lastname: "",
    email: "",
    password: "",
    phoneNumber: "",
    type: "individual",
    companyName: "",
  });

  // Initialize session on mount
  useEffect(() => {
    const fullUrl = window.location.href;

    const encodedEmail = extractEmailParam(fullUrl);

    const initializeSession = async () => {
      try {
        if (!encodedEmail) {
          console.warn('Email non trouvé');
          return;
        }
        const decodedEmail = decodeURIComponent(encodedEmail);
        const email = decryptApiBodyToStringWordPress(decodedEmail);

        setGlobalEmail(email);

        // First call to server to generate session token
        const response = await tempRegister({email, isNewUserFromWordpress: true, type: 'individual'});

        if (response?.sessionPageToken) {
          setSessionPageToken(response.sessionPageToken);
          localStorageUtil.set(localStorageUtil.KEYS.REGISTRATION_SESSION, response.sessionPageToken);
        }
      } catch (error) {
        console.error("Failed to initialize session:", error);
        const fallbackToken = 'temp_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
        setSessionPageToken(fallbackToken);
        localStorageUtil.set(localStorageUtil.KEYS.REGISTRATION_SESSION, fallbackToken);
      } finally {
        setIsInitialized(true);
      }
    };

    const savedToken  = localStorageUtil.get(localStorageUtil.KEYS.REGISTRATION_SESSION);

    if (savedToken) {
      setSessionPageToken(savedToken);
      setIsInitialized(true);
    } else {
      initializeSession();
    }

    return () => {
      // Cleanup if needed
    };
  }, []);

  // Create debounced send update function
  const sendPartialUpdate = useCallback(
    debounce(async (updates: any) => {
      if (!sessionPageToken || !isInitialized) return;

      const payload = {
        sessionPageToken: sessionPageToken,
        type: 'individual',
        ...updates
      };

      const cleanPayload: any = {};
      Object.entries(payload).forEach(([key, value]) => {
        if (value !== '' && value !== null && value !== undefined) {
          cleanPayload[key] = value;
        }
      });

      await tempRegister(cleanPayload);
    }, 1000),
    [sessionPageToken, isInitialized]
  );

  // Update form values ref and trigger debounced save
  const handleFieldChange = useCallback((fieldName: string, value: string) => {
    if (!isInitialized) return;

    formValuesRef.current = {
      ...formValuesRef.current,
      [fieldName]: value
    };

    const update = { [fieldName]: value };
    sendPartialUpdate(update);
  }, [sendPartialUpdate, isInitialized]);

  // Step 0 validation schema
  const validationSchemaStep0 = Yup.object({
    firstname: Yup.string()
      .min(2, "Le prénom doit contenir au moins 2 caractères.")
      .max(50, "Le prénom ne peut pas dépasser 50 caractères.")
      .matches(
        /^[A-Za-zÀ-ÖØ-öø-ÿ\s\-']+$/,
        "Seules les lettres, espaces, tirets, apostrophes et accents sont autorisés.",
      )
      .required("Le prénom est requis"),
    lastname: Yup.string()
      .min(2, "Le nom doit contenir au moins 2 caractères.")
      .max(20, "Le nom ne peut pas dépasser 20 caractères.")
      .matches(
        /^[A-Za-zÀ-ÖØ-öø-ÿ\s\-']+$/,
        "Seules les lettres, espaces, tirets, apostrophes et accents sont autorisés.",
      )
      .required("Le nom est requis"),
    phoneNumber: Yup.string()
      .matches(/^0[1-9]\d{8}$/, "Numéro de téléphone français invalide (ex: 0612345678)")
      .required("Le numéro de téléphone est requis"),
  });

  // Formik for Step 0
  const formikStep0 = useFormik({
    initialValues: {
      firstname: '',
      lastname: '',
      phoneNumber: '',
    },
    validationSchema: validationSchemaStep0,
    onSubmit: async (values) => {
      try {
        setIsLoading(true);
        // Final save before proceeding
        if (sendPartialUpdate.flush) {
          sendPartialUpdate.flush();
        }

        // Save registration data
        setRegistrationData(prev => ({
          ...prev,
          firstname: values.firstname,
          lastname: values.lastname,
          phoneNumber: values.phoneNumber,
        }));

        // Call successTemp API with session token
        if (sessionPageToken) {
          try {
            const response = await successTemp({
              sessionPageToken: sessionPageToken,
              firstname: values.firstname,
              lastname: values.lastname,
              phoneNumber: values.phoneNumber,
              email: globalEmail,
              isNewUserFromWordpress: true,
              type: 'individual'
            });

            setSuccessMessage("Un nouveau lien de confirmation vous a été envoyé par email.");
            setErrorMessage("");
            // Clean up local storage
            localStorage.removeItem('registrationSessionToken');
            // Proceed to next step
            globalSetSteps(3);
            setIsLoading(false);
          } catch (error: any) {
            setSuccessMessage("")
            setErrorMessage(error.response.data.error);
            setIsLoading(false);
            // Handle error
          }
        }
      } catch (error) {
        console.error('Registration error:', error);
      }
    },
  });

  // Wrap formik's handleChange to include debounced save
  const customHandleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    formikStep0.handleChange(e);
    handleFieldChange(name, value);
  };

  // Also handle blur for final save
  const customHandleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    formikStep0.handleBlur(e);

    const { name, value } = e.target;
    if (sessionPageToken && isInitialized && value.trim() !== '') {
      tempRegister({
        sessionPageToken: sessionPageToken,
        type: 'individual',
        [name]: value
      });
    }
  };

  // Helper function to format phone number
  const getFullPhoneNumber = (phone: string) => {
    return `+33${phone.substring(1)}`;
  };

  const extractEmailParam = (url: string): string | null => {
    try {
      // Trouver la partie après ?email= et avant le prochain & ou la fin
      const match = url.match(/[?&]email=([^&]*)/);
      return match ? match[1] : null;
    } catch {
      return null;
    }
  };

  // Get email from URL - Version corrigée pour les +
  useEffect(() => {
    // 1. Récupérer l'URL complète
    const fullUrl = window.location.href;

    // 2. Extraire le paramètre email en évitant URLSearchParams qui transforme les +

    const encodedEmail = extractEmailParam(fullUrl);

    if (!encodedEmail) {
      console.warn('Email non trouvé');
      return;
    }

    // 3. Décoder URI (transforme %2B en +, %3D en =, etc.)
    const decodedEmail = decodeURIComponent(encodedEmail);

    // 4. Décrypter
    const email = decryptApiBodyToStringWordPress(decodedEmail);

    if (email) {
      setGlobalEmail(email);

      if (sessionPageToken && isInitialized) {
        setRegistrationData(prev => ({ ...prev, email }));
        tempRegister({ sessionPageToken, email });
      }
    }
  }, [sessionPageToken, isInitialized]);

  // Render Step 0: Personal Information
  const renderStep0 = () => (
    <form onSubmit={formikStep0.handleSubmit} className="registration-form__form">
      <FormInput
        label="Prénom *"
        placeholder="Votre prénom"
        name="firstname"
        icon={<IconUser
          width={16}
          height={16}
          fill="#1956A8"
        />}
        required
        value={formikStep0.values.firstname}
        onChange={customHandleChange}
        onBlur={customHandleBlur}
        error={formikStep0.touched.firstname && formikStep0.errors.firstname}
      />


      <FormInput
        label="Nom *"
        placeholder="Votre nom"
        name="lastname"
        icon={<IconUser
          width={16}
          height={16}
          fill="#1956A8"
        />}
        required
        value={formikStep0.values.lastname}
        onChange={customHandleChange}
        onBlur={customHandleBlur}
        error={formikStep0.touched.lastname && formikStep0.errors.lastname}
      />

      <FormInput
        label="Numéro de téléphone *"
        placeholder="06 12 34 56 78"
        name="phoneNumber"
        type="tel"
        icon={<IconPhone
          width={16}
          height={16}
          fill="#1956A8"
        />}
        required
        value={formikStep0.values.phoneNumber}
        onChange={customHandleChange}
        onBlur={customHandleBlur}
        error={formikStep0.touched.phoneNumber && formikStep0.errors.phoneNumber}
      />

      <Checkbox
        id="terms"
        name="terms"
        required
        checked={isTermsAccepted}
        onChange={() => setIsTermsAccepted(!isTermsAccepted)}
        label={
          <div>
            En validant ce formulaire, vous acceptez nos{' '}
            <a href="https://idprotect.fr/cgv-cgu" target={'_blank'} className="registration-form__link">
              conditions générales d'utilisation
            </a>{' '}
            et notre{' '}
            <a href="https://idprotect.fr/politique-de-confidentialites" target={'_blank'} className="registration-form__link">
              politique de confidentialité
            </a>
            .
          </div>
        }
      />

      <Button
        type="submit"
        variant="secondary"
        size="medium"
        className="registration-form__submit"
        disabled={!formikStep0.isValid || !formikStep0.dirty || !isTermsAccepted}
        loading={isLoading}
      >
        Suivant
      </Button>
    </form>
  );

  // Main render
  return (
    <div className="registration-form">
      <h2 className="registration-form__title">
        FINALISER VOTRE INSCRIPTION
      </h2>
      <p className="registration-form__subtitle">
        Complétez vos informations pour activer ID Tracker
      </p>

      {errorMessage !== "" && (<Alert type="error">{errorMessage}</Alert>)}
      {successMessage !== "" && <Alert type="success">{successMessage}</Alert>}

      <div className="registration-form__forms">
        {currentStep === 0 && renderStep0()}
      </div>
    </div>
  );
};

export default RegistrationForm;